import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Loader2, Search } from "lucide-react";
import { HeroSlides } from "@/components/home/HeroSlides";
import { SuggestionList, useSuggestions } from "@/components/home/SmartSearch";
import { AiSparkle } from "@/components/common/AiSparkle";
import { UsFlag } from "@/components/common/UsFlag";
import { useBases } from "@/hooks/useData";
import { useConcierge } from "@/context/ConciergeContext";
import { api, errorMessage } from "@/lib/api";
import { PropertyAerialDialog } from "@/components/map/PropertyAerialDialog";
import { PropertySuggestions, propertyAddress, usePropertySuggestions } from "@/components/home/PropertySuggestions";

const AI_EXAMPLES = [
  "How far is the closest Italian restaurant from Ghent?",
  "3 bed homes under $450K within 15 minutes of NAS Oceana",
  "Which elementary school is closest to Town Center?",
  "How far is Chesapeake from Naval Station Norfolk?",
];

/** Rotating example question that opens the AI concierge (marketing the AI right under the search). */
const AiPromo = ({ onAsk }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((x) => (x + 1) % AI_EXAMPLES.length), 3800);
    return () => clearTimeout(t);
  }, [i]);
  return (
    <button type="button" onClick={() => onAsk(AI_EXAMPLES[i])} data-sfx="chime" data-testid="hero-ai-promo"
      className="group mt-4 flex max-w-full items-center gap-2.5 rounded-full border border-white/30 bg-black/35 py-2 pl-2 pr-4 text-left text-[13.5px] text-white backdrop-blur-md transition-colors hover:bg-black/55">
      <span className="ai-glow shrink-0 rounded-full"><span className="flex items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider"><AiSparkle className="h-3.5 w-3.5" /> New</span></span>
      <span className="min-w-0 truncate"><span className="text-white/75">Ask our AI: </span><span key={i} className="animate-rise-in">“{AI_EXAMPLES[i]}”</span></span>
    </button>
  );
};

const QUICK = ["ns-norfolk", "nas-oceana", "jeb-little-creek", "langley", "nnsy", "fort-gregg-adams"];
const TABS = [
  { id: "buy", label: "Buy" },
  { id: "sell", label: "Sell" },
  { id: "ai", label: "Ask AI", short: "AI" },
  { id: "pcs", label: "PCS" },
  { id: "aerial", label: "3D View", short: "3D" },
];

export function matchBase(bases, term) {
  const t = term.trim().toLowerCase();
  if (!t) return null;
  return bases.find((b) => b.name.toLowerCase().includes(t) || b.short.toLowerCase().includes(t) || b.aliases.some((a) => a.includes(t) || t.includes(a)));
}

export const Hero = () => {
  const navigate = useNavigate();
  const { openWith } = useConcierge();
  const bases = useBases();
  const [tab, setTab] = useState("buy");
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [aerialListing, setAerialListing] = useState(null);
  const [aerialLoading, setAerialLoading] = useState(false);
  const [aerialError, setAerialError] = useState("");
  const sugg = useSuggestions(q, tab === "buy" && focused);
  const propertySuggestions = usePropertySuggestions(q, tab === "aerial" && focused);

  const submit = async (e) => {
    e.preventDefault();
    if (tab === "sell") return navigate("/sellers#market-analysis");
    if (tab === "pcs") return navigate("/military#pcs-form");
    if (tab === "ai") return openWith(q.trim().length >= 3 ? q.trim() : undefined);
    if (tab === "aerial") {
      if (!q.trim()) { setAerialError("Enter an address, neighborhood, ZIP, or MLS number."); return; }
      setAerialLoading(true); setAerialError("");
      try {
        const { data } = await api.get("/listings", { params: { q: q.trim(), limit: 8 } });
        const listing = data?.listings?.[0];
        if (!listing) { setAerialError("No matching property was found. Try its neighborhood, ZIP, or MLS number."); return; }
        setAerialListing(listing);
      } catch (error) { setAerialError(errorMessage(error)); } finally { setAerialLoading(false); }
      return;
    }
    const base = matchBase(bases, q);
    if (base) return navigate(`/search?base=${base.id}&radius=15&sort=distance`);
    return navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : "/search");
  };

  const placeholder = {
    buy: "Base, city, neighborhood or ZIP — try “Oceana”",
    ai: "Ask anything: “closest Italian restaurant to Ghent?”",
    pcs: "Tell us where you're reporting",
    sell: "Your home's address for a free market analysis",
    aerial: "Address, neighborhood, ZIP, or MLS number",
  }[tab];

  return (
    <section className="relative h-[100svh] min-h-[760px] max-h-[1000px] w-full overflow-hidden bg-neutral-900 lg:min-h-[700px]" data-testid="hero">
      <HeroSlides />

      <div className="relative z-10 mx-auto flex h-full max-w-[1240px] flex-col items-center justify-center px-5 pb-44 pt-20 text-center sm:px-8 lg:pb-28">
        <p className="eyebrow animate-rise-in !text-brass">Hampton Roads & Greater Richmond · Military relocation</p>
        <h1 className="mt-4 animate-rise-in font-display text-[44px] font-normal leading-[1.02] text-white [animation-delay:120ms] sm:text-6xl lg:text-7xl">
          Orders in hand? <br className="hidden sm:block" />Let's find home.
        </h1>
        <p className="mt-5 max-w-2xl animate-rise-in text-base text-white/85 [animation-delay:220ms] sm:text-lg">
          Search homes by distance to every base and shipyard, with the quality and customer support of
          The Rob Resh Team, led by a 30-year retired Navy Senior Chief.
        </p>

        <form onSubmit={submit} className="mt-9 w-full max-w-[640px] animate-rise-in text-left [animation-delay:320ms]" data-testid="hero-search-form">
          <div className="mb-2 grid w-full grid-cols-5 gap-0.5 rounded-full border border-white/20 bg-black/45 p-1 backdrop-blur-md sm:gap-1" data-testid="hero-tabs">
            {TABS.map((t) => (
              <button key={t.id} type="button" onClick={() => { setTab(t.id); setAerialError(""); }} data-testid={`hero-tab-${t.id}`} data-sfx={t.id === "ai" ? "chime" : undefined}
                className={`relative flex min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-1 py-2 text-[12.5px] font-medium transition-colors sm:px-3 sm:text-[13.5px] ${tab === t.id ? (t.id === "ai" ? "ai-fill text-ink" : "bg-white text-ink shadow") : "text-white/85 hover:bg-white/15 hover:text-white"}`}>
                {t.id === "ai" && <AiSparkle className="hidden h-4 w-4 sm:block" />}{t.id === "pcs" && <UsFlag className="hidden h-[11px] w-[18px] rounded-[1px] shadow-sm sm:block" />}{t.id === "aerial" && <Box className="hidden h-4 w-4 sm:block" />}<span className={`relative ${t.id === "ai" || t.id === "aerial" ? `hero-tab-pop hero-tab-pop-${t.id}` : ""} ${tab === t.id ? "is-active" : ""}`} data-testid={`hero-tab-label-${t.id}`}><span className="sm:hidden">{t.short || t.label}</span><span className="hidden sm:inline">{t.label}</span></span>{(t.id === "ai" || t.id === "aerial") && tab !== t.id && <span className="hero-tab-ping" aria-hidden="true" />}
              </button>
            ))}
          </div>
          <div className={tab === "ai" ? "ai-glow ai-halo" : ""} data-testid="hero-search-box">
          <div className={`relative flex items-center gap-2 p-2 shadow-2xl ${tab === "ai" ? "ai-fill rounded" : "bg-white"}`}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid="hero-search-input"
              onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} autoComplete="off"
              className="h-11 min-w-0 flex-1 bg-transparent px-3 text-[16px] text-ink placeholder:text-neutral-500 focus:outline-none" />
            <button type="submit" disabled={aerialLoading} aria-label="Search" data-testid="hero-search-submit" data-sfx={tab === "ai" ? "chime" : undefined}
              className="grid h-11 w-11 shrink-0 place-items-center bg-ink text-white transition-colors hover:bg-neutral-800">
              {aerialLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : tab === "ai" ? <AiSparkle className="h-5 w-5" /> : <Search className="h-4 w-4" />}
            </button>
            {focused && tab !== "aerial" && (
              <SuggestionList {...sugg}
                onBase={(b) => navigate(`/search?base=${b.id}&radius=15&sort=distance`)}
                onPlace={(p) => navigate(`/search?q=${encodeURIComponent(p.label)}`)} />
            )}
            {focused && tab === "aerial" && <PropertySuggestions listings={propertySuggestions} onChoose={(listing) => { setQ(propertyAddress(listing)); setFocused(false); setAerialError(""); setAerialListing(listing); }} />}
          </div>
          </div>
          {aerialError && <p className="mt-3 text-center text-sm text-white" data-testid="hero-aerial-error">{aerialError}</p>}
        </form>
        <AiPromo onAsk={(q) => openWith(q)} />

        <div className="mt-5 flex max-w-3xl animate-rise-in flex-wrap justify-center gap-2 [animation-delay:420ms]">
          <span className="py-1.5 text-[13px] text-white/70">Search near:</span>
          {QUICK.map((id, index) => {
            const b = bases.find((x) => x.id === id);
            return b ? (
              <Link key={id} to={`/search?base=${id}&radius=15&sort=distance`} data-testid={`hero-quick-${id}`}
                className={`${index > 2 ? "hidden sm:inline-block" : ""} rounded-full border border-white/40 bg-white/10 px-3.5 py-1.5 text-[13px] text-white backdrop-blur transition-colors hover:bg-white hover:text-ink`}>
                {b.short}
              </Link>
            ) : null;
          })}
        </div>
      </div>
      {aerialListing && <PropertyAerialDialog listing={aerialListing} onClose={() => setAerialListing(null)} />}
    </section>
  );
};
